import type { SharedMarketingProps } from '@/types/marketing'

interface PolicySection {
  title: string
  content: string
  bullets: string[]
  extra: string | null
}

interface AiMusicPolicyProps extends SharedMarketingProps {
  last_updated: string
  sections: PolicySection[]
}

function Section({ section }: { section: PolicySection }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-white">{section.title}</h2>
      <p className="text-sm leading-7 text-white/72">{section.content}</p>
      {section.bullets.length > 0 && (
        <ul className="list-disc space-y-1.5 pl-6 text-sm leading-7 text-white/72">
          {section.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
      {section.extra && (
        <p className="text-sm leading-7 text-white/72">{section.extra}</p>
      )}
    </section>
  )
}

export default function AiMusicPolicy(props: AiMusicPolicyProps) {
  return (
    <div className="container mx-auto px-6 pb-20 pt-32 lg:pt-40">
      <div className="mx-auto max-w-3xl">
        <header className="mb-12">
          <h1 className="marketing-display text-3xl lg:text-4xl">AI Music Policy</h1>
          <p className="mt-4 text-sm text-white/50">
            Last updated: {props.last_updated}
          </p>
          <p className="mt-6 text-sm leading-7 text-white/72">
            This policy explains what kinds of AI involvement we accept in releases distributed through
            wavbee, what we do not, and what we expect you to disclose at submission. It applies to
            every plan and every release — independent artists, labels, and enterprise accounts alike.
          </p>
        </header>

        <div className="space-y-10">
          {props.sections.map((section) => (
            <Section key={section.title} section={section} />
          ))}
        </div>

        <footer className="mt-14 rounded-lg border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-lg font-semibold text-white">Questions?</h2>
          <p className="mt-2 text-sm leading-7 text-white/72">
            If you are unsure whether a specific tool, model, or workflow is acceptable, email
            us at <a href="mailto:help@wavbee.com" className="text-[#ff6a1a] hover:underline">help@wavbee.com</a> before
            submitting. We would rather answer a question up front than reject a release after the fact.
          </p>
        </footer>
      </div>
    </div>
  )
}
